import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ChevronDown, ChevronUp, Eye, EyeOff, GripVertical, Layers, Pencil, Plus, Trash2, UtensilsCrossed } from 'lucide-react';
import { hhmmToMinutes, minutesToHHMM, type Permission } from '@garlic/shared';
import { api, errorMessage, fieldErrors, patch, post, put } from '../../lib/api';
import { formatBRL } from '../../lib/format';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea, cx } from '../../ui/primitives';
import { Dialog, useToast } from '../../ui/feedback';
import { ImageField, MoneyInput, Switch, centsToInput, inputToCents } from '../../ui/controls';

type Category = { id: string; name: string; description: string | null; imagePath: string | null; sortOrder: number; visible: boolean; availableFrom: number | null; availableTo: number | null; archivedAt: string | null };
type Product = {
  id: string; categoryId: string; stationId: string; name: string; description: string | null; imagePath: string | null; priceCents: number;
  available: boolean; featured: boolean; prepMinutes: number | null; allowNotes: boolean; maxQuantity: number; trackStock: boolean; stockQuantity: number | null;
  sortOrder: number; archivedAt: string | null; optionGroupIds: string[];
};
type Option = { id: string; name: string; priceCents: number; available: boolean };
type Group = { id: string; name: string; description: string | null; minSelect: number; maxSelect: number; options: Option[]; productCount: number };
type Station = { id: string; name: string; kind: string };
type MenuAdmin = { categories: Category[]; products: Product[]; optionGroups: Group[]; stations: Station[] };

export function StaffMenu({ permissions }: { permissions: Permission[] }) {
  const canEdit = permissions.includes('menu.edit');
  const canToggle = permissions.includes('menu.availability');
  const [tab, setTab] = useState<'products' | 'groups'>('products');
  const [showArchived, setShowArchived] = useState(false);
  const q = useQuery({ queryKey: ['staff-menu', showArchived], queryFn: () => api<MenuAdmin>(`/api/staff/menu?archived=${showArchived ? 1 : 0}`) });

  return (
    <>
      <PageHeader title="Cardápio" description="Categorias, produtos e adicionais. Mudanças aparecem para os clientes na hora." />
      <div role="tablist" className="mb-6 flex gap-1 border-b border-line">
        {([['products', 'Categorias e produtos'], ['groups', 'Grupos de adicionais']] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cx('-mb-px border-b-2 px-3 py-2.5 text-sm font-medium', tab === k ? 'border-garlic-600 text-garlic-700' : 'border-transparent text-ink-3 hover:text-ink')}>
            {l}
          </button>
        ))}
      </div>
      {q.isPending && <Spinner />}
      {q.isError && <Alert tone="danger">{errorMessage(q.error)}</Alert>}
      {q.data && tab === 'products' && <ProductsTab data={q.data} canEdit={canEdit} canToggle={canToggle} showArchived={showArchived} setShowArchived={setShowArchived} />}
      {q.data && tab === 'groups' && <GroupsTab data={q.data} canEdit={canEdit} />}
    </>
  );
}

/* ───────── Categorias e produtos ───────── */

function ProductsTab({ data, canEdit, canToggle, showArchived, setShowArchived }: { data: MenuAdmin; canEdit: boolean; canToggle: boolean; showArchived: boolean; setShowArchived: (v: boolean) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | undefined>(data.categories[0]?.id);
  const [catDialog, setCatDialog] = useState<Category | 'new' | null>(null);
  const [prodDialog, setProdDialog] = useState<Product | 'new' | null>(null);
  const [order, setOrder] = useState<Category[]>(data.categories);
  const [dragId, setDragId] = useState<string | null>(null);
  useEffect(() => setOrder(data.categories), [data.categories]);
  useEffect(() => {
    if (!selectedId || !data.categories.some((c) => c.id === selectedId)) setSelectedId(data.categories[0]?.id);
  }, [data.categories, selectedId]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['staff-menu'] });
  const selected = data.categories.find((c) => c.id === selectedId);
  const products = data.products.filter((p) => p.categoryId === selectedId);
  const station = (id: string) => data.stations.find((s) => s.id === id)?.name ?? '—';

  const saveOrder = async (cats: Category[]) => {
    try {
      await put('/api/staff/menu/categories/order', { ids: cats.filter((c) => !c.archivedAt).map((c) => c.id) });
      await refresh();
    } catch (e) {
      toast.error(errorMessage(e));
      setOrder(data.categories);
    }
  };
  const move = (id: string, dir: -1 | 1) => {
    const i = order.findIndex((c) => c.id === id);
    const j = i + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setOrder(next);
    void saveOrder(next);
  };
  const action = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      await refresh();
      toast.success(msg);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  if (data.categories.length === 0 && !showArchived) {
    return (
      <Card>
        <EmptyState icon={<UtensilsCrossed className="size-5" />} title="Seu cardápio está vazio" action={canEdit && <Button icon={<Plus className="size-4" />} onClick={() => setCatDialog('new')}>Criar primeira categoria</Button>}>
          Comece pelas categorias (ex.: Entradas, Pratos, Bebidas) e depois cadastre os produtos.
        </EmptyState>
        {catDialog && <CategoryDialog value={catDialog} onClose={() => setCatDialog(null)} onSaved={refresh} />}
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_1fr]">
      <Card className="self-start">
        <CardHeader title="Categorias" action={canEdit && <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => setCatDialog('new')}>Nova</Button>} />
        <ul className="p-2" aria-label="Categorias (arraste para reordenar)">
          {order.map((c, i) => (
            <li
              key={c.id}
              draggable={canEdit && !c.archivedAt}
              onDragStart={() => setDragId(c.id)}
              onDragOver={(e) => {
                e.preventDefault();
                if (!dragId || dragId === c.id) return;
                const from = order.findIndex((x) => x.id === dragId);
                const next = [...order];
                const [moved] = next.splice(from, 1);
                next.splice(i, 0, moved!);
                setOrder(next);
              }}
              onDragEnd={() => {
                setDragId(null);
                if (order.map((x) => x.id).join() !== data.categories.map((x) => x.id).join()) void saveOrder(order);
              }}
              className={cx('group flex items-center gap-1 rounded-xl', dragId === c.id && 'opacity-50', selectedId === c.id ? 'bg-paper-2' : 'hover:bg-paper')}
            >
              {canEdit && !c.archivedAt && <GripVertical className="size-4 shrink-0 cursor-grab text-ink-3" aria-hidden />}
              <button onClick={() => setSelectedId(c.id)} className="min-w-0 flex-1 px-2 py-2 text-left text-sm">
                <span className={cx('block truncate font-medium', (c.archivedAt || !c.visible) && 'text-ink-3')}>{c.name}</span>
                <span className="block text-xs text-ink-3">
                  {data.products.filter((p) => p.categoryId === c.id && !p.archivedAt).length} produto(s)
                  {c.archivedAt ? ' · arquivada' : !c.visible ? ' · oculta' : c.availableFrom != null ? ` · ${minutesToHHMM(c.availableFrom)}–${minutesToHHMM(c.availableTo!)}` : ''}
                </span>
              </button>
              {canEdit && !c.archivedAt && (
                <span className="flex flex-col pr-1 opacity-0 focus-within:opacity-100 group-hover:opacity-100">
                  <button aria-label={`Subir ${c.name}`} onClick={() => move(c.id, -1)} className="rounded p-0.5 text-ink-3 hover:text-ink"><ChevronUp className="size-3.5" /></button>
                  <button aria-label={`Descer ${c.name}`} onClick={() => move(c.id, 1)} className="rounded p-0.5 text-ink-3 hover:text-ink"><ChevronDown className="size-3.5" /></button>
                </span>
              )}
            </li>
          ))}
        </ul>
        <label className="flex items-center gap-2 border-t border-line px-4 py-3 text-xs text-ink-3">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-garlic-600" /> Mostrar arquivados
        </label>
      </Card>

      <Card>
        {selected ? (
          <>
            <CardHeader
              title={selected.name}
              description={selected.description ?? undefined}
              action={
                canEdit && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="ghost" icon={<Pencil className="size-4" />} onClick={() => setCatDialog(selected)}>Editar</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={selected.archivedAt ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
                      onClick={() => action(() => post(`/api/staff/menu/categories/${selected.id}/archive`, { archived: !selected.archivedAt }), selected.archivedAt ? 'Categoria restaurada' : 'Categoria arquivada')}
                    >
                      {selected.archivedAt ? 'Restaurar' : 'Arquivar'}
                    </Button>
                    {!selected.archivedAt && <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setProdDialog('new')}>Produto</Button>}
                  </div>
                )
              }
            />
            {products.length === 0 ? (
              <EmptyState title="Nenhum produto nesta categoria" />
            ) : (
              <ul className="divide-y divide-line">
                {products.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-5 py-3">
                    {p.imagePath ? <img src={p.imagePath} alt="" className="size-12 shrink-0 rounded-lg object-cover" /> : <div className="size-12 shrink-0 rounded-lg bg-paper-2" />}
                    <div className="min-w-0 flex-1">
                      <p className={cx('truncate font-medium', p.archivedAt && 'text-ink-3 line-through')}>{p.name}</p>
                      <p className="text-xs text-ink-3">
                        {formatBRL(p.priceCents)} · {station(p.stationId)}
                        {p.optionGroupIds.length > 0 && ` · ${p.optionGroupIds.length} grupo(s) de adicionais`}
                        {p.trackStock && ` · estoque ${p.stockQuantity}`}
                      </p>
                    </div>
                    {p.featured && <Badge tone="brand">Destaque</Badge>}
                    {p.trackStock && (p.stockQuantity ?? 0) <= 0 && <Badge tone="danger">Esgotado</Badge>}
                    {!p.archivedAt && (
                      <div className="flex items-center gap-2" title={p.available ? 'Disponível' : 'Pausado'}>
                        <span className="hidden text-xs text-ink-3 sm:inline">{p.available ? 'Disponível' : 'Pausado'}</span>
                        <Switch
                          checked={p.available}
                          disabled={!canToggle}
                          label={`${p.name} disponível`}
                          onChange={(v) => action(() => post(`/api/staff/menu/products/${p.id}/availability`, { available: v }), v ? `${p.name} liberado` : `${p.name} pausado`)}
                        />
                      </div>
                    )}
                    {canEdit && (
                      <div className="flex">
                        <Button size="sm" variant="ghost" aria-label={`Editar ${p.name}`} icon={<Pencil className="size-4" />} onClick={() => setProdDialog(p)} />
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={p.archivedAt ? `Restaurar ${p.name}` : `Arquivar ${p.name}`}
                          icon={p.archivedAt ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
                          onClick={() => action(() => post(`/api/staff/menu/products/${p.id}/archive`, { archived: !p.archivedAt }), p.archivedAt ? 'Produto restaurado' : 'Produto arquivado (histórico de vendas mantido)')}
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <EmptyState title="Selecione uma categoria" />
        )}
      </Card>

      {catDialog && <CategoryDialog value={catDialog} onClose={() => setCatDialog(null)} onSaved={refresh} />}
      {prodDialog && selected && <ProductDialog value={prodDialog} data={data} categoryId={selected.id} onClose={() => setProdDialog(null)} onSaved={refresh} />}
    </div>
  );
}

function CategoryDialog({ value, onClose, onSaved }: { value: Category | 'new'; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const c = value === 'new' ? null : value;
  const [f, setF] = useState({
    name: c?.name ?? '', description: c?.description ?? '', imagePath: c?.imagePath ?? null, visible: c?.visible ?? true,
    window: c?.availableFrom != null, from: c?.availableFrom != null ? minutesToHHMM(c.availableFrom) : '11:00', to: c?.availableTo != null ? minutesToHHMM(c.availableTo) : '15:00',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const from = f.window ? hhmmToMinutes(f.from) : null;
    const to = f.window ? hhmmToMinutes(f.to) : null;
    if (f.window && (from === null || to === null)) return setErrors({ availableTo: 'Use o formato HH:MM' });
    setBusy(true);
    try {
      const body = { name: f.name, description: f.description, imagePath: f.imagePath, visible: f.visible, availableFrom: from, availableTo: to };
      if (c) await patch(`/api/staff/menu/categories/${c.id}`, body);
      else await post('/api/staff/menu/categories', body);
      toast.success(c ? 'Categoria atualizada' : 'Categoria criada');
      onSaved();
      onClose();
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title={c ? 'Editar categoria' : 'Nova categoria'} footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button type="submit" form="cat-form" loading={busy}>Salvar</Button></>}>
      <form id="cat-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Nome" error={errors.name}>{(id) => <Input id={id} autoFocus value={f.name} invalid={!!errors.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: Pratos principais" />}</Field>
        <Field label="Descrição (opcional)">{(id) => <Textarea id={id} className="min-h-16" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />}</Field>
        <ImageField value={f.imagePath} onChange={(imagePath) => setF({ ...f, imagePath })} />
        <label className="flex items-center justify-between gap-3 rounded-xl border border-line px-3 py-2.5 text-sm">
          <span className="inline-flex items-center gap-2">{f.visible ? <Eye className="size-4" /> : <EyeOff className="size-4" />} Visível no cardápio</span>
          <Switch checked={f.visible} onChange={(visible) => setF({ ...f, visible })} label="Visível no cardápio" />
        </label>
        <div className="rounded-xl border border-line p-3">
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Disponível só em um horário (ex.: almoço)</span>
            <Switch checked={f.window} onChange={(window) => setF({ ...f, window })} label="Disponibilidade por horário" />
          </label>
          {f.window && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="Das">{(id) => <Input id={id} type="time" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />}</Field>
              <Field label="Até" error={errors.availableTo}>{(id) => <Input id={id} type="time" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />}</Field>
            </div>
          )}
        </div>
      </form>
    </Dialog>
  );
}

function ProductDialog({ value, data, categoryId, onClose, onSaved }: { value: Product | 'new'; data: MenuAdmin; categoryId: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const p = value === 'new' ? null : value;
  const kitchen = data.stations.find((s) => s.kind === 'KITCHEN') ?? data.stations[0];
  const [f, setF] = useState({
    name: p?.name ?? '', description: p?.description ?? '', imagePath: p?.imagePath ?? null, price: p ? centsToInput(p.priceCents) : '',
    categoryId: p?.categoryId ?? categoryId, stationId: p?.stationId ?? kitchen?.id ?? '', available: p?.available ?? true, featured: p?.featured ?? false,
    prepMinutes: p?.prepMinutes ? String(p.prepMinutes) : '', allowNotes: p?.allowNotes ?? true, maxQuantity: String(p?.maxQuantity ?? 20),
    trackStock: p?.trackStock ?? false, stockQuantity: p?.stockQuantity != null ? String(p.stockQuantity) : '', optionGroupIds: p?.optionGroupIds ?? [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => {
    setF((s) => ({ ...s, [k]: v }));
    setErrors((e) => ({ ...e, [k]: '' }));
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const priceCents = inputToCents(f.price);
    if (priceCents === null || f.price.trim() === '') return setErrors({ priceCents: 'Informe o preço. Ex.: 32,90' });
    setBusy(true);
    try {
      const body = {
        name: f.name, description: f.description, imagePath: f.imagePath, priceCents, categoryId: f.categoryId, stationId: f.stationId,
        available: f.available, featured: f.featured, prepMinutes: f.prepMinutes ? Number(f.prepMinutes) : null, allowNotes: f.allowNotes,
        maxQuantity: Number(f.maxQuantity) || 20, trackStock: f.trackStock, stockQuantity: f.trackStock ? Number(f.stockQuantity) : null, optionGroupIds: f.optionGroupIds,
      };
      if (p) await patch(`/api/staff/menu/products/${p.id}`, body);
      else await post('/api/staff/menu/products', body);
      toast.success(p ? 'Produto atualizado' : 'Produto criado');
      onSaved();
      onClose();
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title={p ? `Editar ${p.name}` : 'Novo produto'} footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button type="submit" form="prod-form" loading={busy}>Salvar</Button></>}>
      <form id="prod-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Nome" error={errors.name}>{(id) => <Input id={id} autoFocus value={f.name} invalid={!!errors.name} onChange={(e) => set('name', e.target.value)} />}</Field>
        <Field label="Descrição (opcional)" error={errors.description}>{(id) => <Textarea id={id} className="min-h-16" value={f.description} onChange={(e) => set('description', e.target.value)} />}</Field>
        <ImageField value={f.imagePath} onChange={(v) => set('imagePath', v)} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Preço" error={errors.priceCents}>{(id) => <MoneyInput id={id} value={f.price} invalid={!!errors.priceCents} onChange={(v) => set('price', v)} />}</Field>
          <Field label="Setor de produção" error={errors.stationId} hint="Para onde vai a comanda deste item">
            {(id) => <Select id={id} value={f.stationId} onChange={(e) => set('stationId', e.target.value)}>{data.stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>}
          </Field>
          <Field label="Categoria">
            {(id) => <Select id={id} value={f.categoryId} onChange={(e) => set('categoryId', e.target.value)}>{data.categories.filter((c) => !c.archivedAt).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>}
          </Field>
          <Field label="Tempo de preparo (min, opcional)" error={errors.prepMinutes}>{(id) => <Input id={id} type="number" min={1} max={240} value={f.prepMinutes} onChange={(e) => set('prepMinutes', e.target.value)} />}</Field>
          <Field label="Máximo por pedido" error={errors.maxQuantity}>{(id) => <Input id={id} type="number" min={1} max={99} value={f.maxQuantity} onChange={(e) => set('maxQuantity', e.target.value)} />}</Field>
        </div>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-ink-2">Adicionais e personalizações</legend>
          {data.optionGroups.length === 0 ? (
            <p className="text-xs text-ink-3">Nenhum grupo cadastrado. Crie em “Grupos de adicionais”.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {data.optionGroups.map((g) => (
                <label key={g.id} className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-line px-3 py-2 text-sm hover:bg-paper">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-4 accent-garlic-600"
                    checked={f.optionGroupIds.includes(g.id)}
                    onChange={(e) => set('optionGroupIds', e.target.checked ? [...f.optionGroupIds, g.id] : f.optionGroupIds.filter((x) => x !== g.id))}
                  />
                  <span>
                    {g.name}
                    <span className="block text-xs text-ink-3">{g.minSelect > 0 ? 'Obrigatório' : 'Opcional'} · {g.options.length} opções</span>
                  </span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <div className="space-y-2 rounded-xl border border-line p-3 text-sm">
          {([
            ['available', 'Disponível para pedido'],
            ['featured', 'Destacar no cardápio'],
            ['allowNotes', 'Aceita observações do cliente'],
            ['trackStock', 'Controlar estoque'],
          ] as const).map(([k, l]) => (
            <label key={k} className="flex items-center justify-between gap-3">
              <span>{l}</span>
              <Switch checked={f[k]} onChange={(v) => set(k, v)} label={l} />
            </label>
          ))}
          {f.trackStock && (
            <Field label="Quantidade em estoque" error={errors.stockQuantity}>{(id) => <Input id={id} type="number" min={0} value={f.stockQuantity} invalid={!!errors.stockQuantity} onChange={(e) => set('stockQuantity', e.target.value)} />}</Field>
          )}
        </div>
      </form>
    </Dialog>
  );
}

/* ───────── Grupos de adicionais ───────── */

function GroupsTab({ data, canEdit }: { data: MenuAdmin; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<Group | 'new' | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['staff-menu'] });
  return (
    <Card>
      <CardHeader
        title="Grupos de adicionais"
        description="Reutilizáveis entre produtos. Ex.: Ponto da carne (obrigatório, escolha 1), Extras (opcional, até 3)."
        action={canEdit && <Button size="sm" icon={<Plus className="size-4" />} onClick={() => setDialog('new')}>Novo grupo</Button>}
      />
      {data.optionGroups.length === 0 ? (
        <EmptyState icon={<Layers className="size-5" />} title="Nenhum grupo de adicionais" />
      ) : (
        <ul className="divide-y divide-line">
          {data.optionGroups.map((g) => (
            <li key={g.id} className="flex items-start gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {g.name} <Badge tone={g.minSelect > 0 ? 'brand' : 'neutral'}>{g.minSelect > 0 ? 'Obrigatório' : 'Opcional'}</Badge>
                </p>
                <p className="text-xs text-ink-3">
                  {g.maxSelect === 1 ? 'Escolha 1' : `De ${g.minSelect} a ${g.maxSelect}`} · usado em {g.productCount} produto(s)
                </p>
                <p className="mt-1 text-sm text-ink-2">{g.options.map((o) => `${o.name}${o.priceCents ? ` (+${formatBRL(o.priceCents)})` : ''}${o.available ? '' : ' [pausado]'}`).join(' · ')}</p>
              </div>
              {canEdit && (
                <div className="flex">
                  <Button size="sm" variant="ghost" aria-label={`Editar ${g.name}`} icon={<Pencil className="size-4" />} onClick={() => setDialog(g)} />
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Arquivar ${g.name}`}
                    icon={<Archive className="size-4" />}
                    onClick={async () => {
                      if (!confirm(`Arquivar "${g.name}"? Ele sai de ${g.productCount} produto(s); pedidos antigos não mudam.`)) return;
                      try {
                        await post(`/api/staff/menu/option-groups/${g.id}/archive`);
                        await refresh();
                        toast.success('Grupo arquivado');
                      } catch (e) {
                        toast.error(errorMessage(e));
                      }
                    }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {dialog && <GroupDialog value={dialog} onClose={() => setDialog(null)} onSaved={refresh} />}
    </Card>
  );
}

function GroupDialog({ value, onClose, onSaved }: { value: Group | 'new'; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const g = value === 'new' ? null : value;
  const [f, setF] = useState({ name: g?.name ?? '', description: g?.description ?? '', minSelect: String(g?.minSelect ?? 0), maxSelect: String(g?.maxSelect ?? 1) });
  const [opts, setOpts] = useState<{ id?: string; name: string; price: string; available: boolean }[]>(
    g?.options.map((o) => ({ id: o.id, name: o.name, price: o.priceCents ? centsToInput(o.priceCents) : '', available: o.available })) ?? [{ name: '', price: '', available: true }],
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const options = opts.map((o) => ({ id: o.id, name: o.name, priceCents: inputToCents(o.price), available: o.available }));
    if (options.some((o) => o.priceCents === null)) return setErrors({ options: 'Preço de opção inválido. Ex.: 4,50' });
    setBusy(true);
    try {
      const body = { name: f.name, description: f.description, minSelect: Number(f.minSelect), maxSelect: Number(f.maxSelect), options };
      if (g) await put(`/api/staff/menu/option-groups/${g.id}`, body);
      else await post('/api/staff/menu/option-groups', body);
      toast.success('Grupo salvo');
      onSaved();
      onClose();
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title={g ? `Editar ${g.name}` : 'Novo grupo de adicionais'} footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button type="submit" form="group-form" loading={busy}>Salvar</Button></>}>
      <form id="group-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Nome do grupo" error={errors.name}>{(id) => <Input id={id} autoFocus placeholder="Ex.: Ponto da carne" value={f.name} invalid={!!errors.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Mínimo de escolhas" error={errors.minSelect} hint="0 = opcional">{(id) => <Input id={id} type="number" min={0} value={f.minSelect} onChange={(e) => setF({ ...f, minSelect: e.target.value })} />}</Field>
          <Field label="Máximo de escolhas" error={errors.maxSelect}>{(id) => <Input id={id} type="number" min={1} value={f.maxSelect} onChange={(e) => setF({ ...f, maxSelect: e.target.value })} />}</Field>
        </div>
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-ink-2">Opções</legend>
          {errors.options && <p className="mb-2 text-xs text-danger-600">{errors.options}</p>}
          <ul className="space-y-2">
            {opts.map((o, i) => (
              <li key={o.id ?? `new-${i}`} className="flex items-center gap-2">
                <Input aria-label={`Opção ${i + 1}`} placeholder="Nome" value={o.name} onChange={(e) => setOpts(opts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <div className="w-32 shrink-0"><MoneyInput value={o.price} onChange={(v) => setOpts(opts.map((x, j) => (j === i ? { ...x, price: v } : x)))} /></div>
                <Switch checked={o.available} label={`Opção ${i + 1} disponível`} onChange={(v) => setOpts(opts.map((x, j) => (j === i ? { ...x, available: v } : x)))} />
                <Button variant="ghost" size="sm" aria-label="Remover opção" disabled={opts.length === 1} icon={<Trash2 className="size-4" />} onClick={() => setOpts(opts.filter((_, j) => j !== i))} />
              </li>
            ))}
          </ul>
          <Button variant="secondary" size="sm" className="mt-2" icon={<Plus className="size-4" />} onClick={() => setOpts([...opts, { name: '', price: '', available: true }])}>Adicionar opção</Button>
          <p className="mt-2 text-xs text-ink-3">Preço vazio = sem custo adicional.</p>
        </fieldset>
      </form>
    </Dialog>
  );
}
